import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { isPaymentRequired } from "../../api/errors";
import { useCreateInstance, useProviderCatalog, useProviderCredentials } from "../../api/queries";
import type { Instance } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { canSeeBilling } from "../../auth/roleView";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { PROVIDER_LABEL } from "../provider-credentials/ProviderCredentialsPage";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface ProvisionInstanceModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the freshly-created `Instance` right before the modal
   * closes on success — lets a caller continue the flow (Host/Deploy UX
   * Phase 3's "provision flow continuity": don't just toast + dump to a
   * static list, transition into the new host's live progress view). Optional
   * and stateless on purpose: this modal is also reused from the raw
   * Instances (advanced) page, which deliberately stays put rather than
   * navigating anywhere — omitting the callback there is the correct,
   * unchanged behavior, not a gap.
   */
  onProvisioned?: (instance: Instance) => void;
}

function locationLabel(location: { name: string; city: string }): string {
  return `${location.city} (${location.name})`;
}

function priceForLocation(
  serverType: { prices: Array<{ location: string; priceMonthlyGross: string }> },
  location: string,
): string | undefined {
  return serverType.prices.find((p) => p.location === location)?.priceMonthlyGross;
}

function serverTypeLabel(
  serverType: { name: string; cores: number; memoryGB: number; diskGB: number },
  price: string | undefined,
): string {
  const specs = `${serverType.cores} vCPU, ${serverType.memoryGB} GB RAM, ${serverType.diskGB} GB SSD`;
  const priceLabel = price ? `€${Number(price).toFixed(2)}/mo` : "price unavailable in this region";
  return `${serverType.name.toUpperCase()} — ${specs} — ${priceLabel}`;
}

export function ProvisionInstanceModal({ open, onClose, onProvisioned }: ProvisionInstanceModalProps) {
  const { session } = useAuth();
  const credentialFieldId = useId();
  const regionFieldId = useId();
  const serverTypeFieldId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateInstance();
  const { data: credentialsPage, isPending: credentialsPending } = useProviderCredentials();

  const [providerCredId, setProviderCredId] = useState("");
  const [region, setRegion] = useState("");
  const [serverType, setServerType] = useState("");

  const {
    data: catalog,
    isPending: catalogPending,
    isError: catalogIsError,
  } = useProviderCatalog(providerCredId || undefined);

  const credentials = credentialsPage?.items ?? [];
  const locations = catalog?.locations ?? [];
  // Filter to types available in the selected region (a type with no price
  // entry for a location is not offered there), then sort cheapest-first.
  const serverTypesSortedByPrice = useMemo(() => {
    const available = catalog?.serverTypes ?? [];
    return available
      .filter((s) => priceForLocation(s, region) !== undefined)
      .sort((a, b) => {
        const priceA = Number(priceForLocation(a, region)!);
        const priceB = Number(priceForLocation(b, region)!);
        return priceA - priceB;
      });
  }, [catalog, region]);

  // Once the catalog loads (or the selected credential changes), default
  // region/server type to the first — and cheapest — available option
  // rather than leaving a stale/hardcoded value selected.
  useEffect(() => {
    if (locations.length === 0) {
      setRegion("");
      return;
    }
    if (!locations.some((l) => l.name === region)) {
      setRegion(locations[0].name);
    }
  }, [locations, region]);

  useEffect(() => {
    if (serverTypesSortedByPrice.length === 0) {
      setServerType("");
      return;
    }
    if (!serverTypesSortedByPrice.some((s) => s.name === serverType)) {
      setServerType(serverTypesSortedByPrice[0].name);
    }
  }, [serverTypesSortedByPrice, serverType]);

  // 402: refused because of a licensed limit, never because anything running
  // was touched (charter §1 safety corollary) — render the server's own
  // plain-language message rather than a generic failure (Q-7).
  const paymentRequired = mutation.isError && isPaymentRequired(mutation.error);
  const canViewBilling = canSeeBilling(session?.role);

  function handleClose() {
    setProviderCredId("");
    setRegion("");
    setServerType("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate(
      { providerCredId, region, serverType },
      {
        onSuccess: (instance) => {
          pushToast({ title: "Provisioning started…", tone: "success" });
          handleClose();
          // After close, not before — handleClose resets local form state,
          // and a caller's navigation (e.g. to the new host's progress view)
          // shouldn't race that reset.
          onProvisioned?.(instance);
        },
      },
    );
  }

  const catalogLoaded = providerCredId.length > 0 && !catalogPending && !catalogIsError;
  const canSubmit =
    providerCredId.length > 0 &&
    region.length > 0 &&
    serverType.length > 0 &&
    catalogLoaded &&
    !mutation.isPending;

  return (
    <Modal open={open} title="Provision an instance" onClose={handleClose}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && !paymentRequired && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't provision this instance."}
          </p>
        )}

        {paymentRequired && mutation.error && (
          <Banner tone="warning" title="Licensed capacity reached">
            {mutation.error.message}
            {canViewBilling && (
              <>
                {" "}
                <Link href="/billing">See your plan</Link>.
              </>
            )}
          </Banner>
        )}

        <label className="form-field" htmlFor={credentialFieldId}>
          <span>Provider credential</span>
          <select
            id={credentialFieldId}
            value={providerCredId}
            onChange={(e) => {
              setProviderCredId(e.target.value);
              setRegion("");
              setServerType("");
            }}
            required
            disabled={credentialsPending}
          >
            <option value="" disabled>
              {credentialsPending ? "Loading credentials…" : "Select a credential"}
            </option>
            {credentials.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label} ({PROVIDER_LABEL[c.kind]})
              </option>
            ))}
          </select>
          {!credentialsPending && credentials.length === 0 && (
            <span className="form-field__hint">
              No provider credentials yet — add one on the Provider Credentials page first.
            </span>
          )}
        </label>

        {providerCredId.length > 0 && catalogPending && (
          <p className="form-field__hint" role="status">
            Loading available regions and server types…
          </p>
        )}

        {providerCredId.length > 0 && catalogIsError && (
          <p className="form-error" role="alert">
            Couldn't load the provider's current pricing and availability. Try selecting the credential again.
          </p>
        )}

        {catalogLoaded && (
          <>
            <label className="form-field" htmlFor={regionFieldId}>
              <span>Region</span>
              <select id={regionFieldId} value={region} onChange={(e) => setRegion(e.target.value)} required>
                {locations.map((l) => (
                  <option key={l.name} value={l.name}>
                    {locationLabel(l)}
                  </option>
                ))}
              </select>
            </label>

            <label className="form-field" htmlFor={serverTypeFieldId}>
              <span>Server type</span>
              <select
                id={serverTypeFieldId}
                value={serverType}
                onChange={(e) => setServerType(e.target.value)}
                required
              >
                {serverTypesSortedByPrice.map((s) => (
                  <option key={s.name} value={s.name}>
                    {serverTypeLabel(s, priceForLocation(s, region))}
                  </option>
                ))}
              </select>
              <span className="form-field__hint">Billed by the provider directly — Comebye never marks this up.</span>
            </label>
          </>
        )}

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Provisioning…" : "Provision"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
